import toast from 'react-hot-toast';
import { EmailSearchSettings } from '../types';

// Function to generate AI subject lines based on business category and product description
export const generateAISubject = (category: string, description: string): string => {
  // Clean the description by removing dashes and colons
  const cleanDescription = description.replace(/[-:]/g, ' ').trim();
  
  // Extract keywords from the description for more relevant subjects
  const keywords = cleanDescription.split(' ')
    .filter(word => word.length > 4)
    .filter(word => 
      !['there', 'their', 'would', 'could', 'should', 'about', 'these'].includes(word.toLowerCase())
    );
  
  // QR code specific subjects
  if (cleanDescription.toLowerCase().includes('qr code')) {
    const qrSubjects = [
      `Transform Your ${category} Experience with QR Technology`,
      `Streamline ${category} Operations with Our QR Solution`,
      `Revolutionize Customer Experience in Your ${category} Business`,
      `Boost Efficiency: Next-Gen QR System for ${category} Businesses`,
      `Seamless Payments & Table Management for Your ${category}`,
      `QR Innovation: The Future of ${category} Customer Interaction`
    ];
    return qrSubjects[Math.floor(Math.random() * qrSubjects.length)];
  }
  
  // Generic but compelling subjects based on business category
  const subjectTemplates = [
    `Revolutionize Your ${category} Experience with Our Solution`,
    `Boost Your ${category} Efficiency with Our Latest Innovation`,
    `Transform How Your ${category} Business Operates`,
    `Exclusive ${category} Solution That Will Save You Time and Money`,
    `Innovative ${category} Tools to Take Your Business to the Next Level`,
    `How We're Changing the Game for ${category} Businesses`,
    `The Future of ${category} Is Here - See What We've Built`
  ];
  
  // Use a keyword in the subject if available
  if (keywords.length > 0 && Math.random() > 0.3) {
    const keyword = keywords[Math.floor(Math.random() * keywords.length)];
    const keywordSubjects = [
      `How Our ${keyword.charAt(0).toUpperCase() + keyword.slice(1)} Solution Can Transform Your ${category} Business`,
      `${keyword.charAt(0).toUpperCase() + keyword.slice(1)}: The Missing Piece in Your ${category} Strategy`,
      `Unlock New ${category} Potential with Our ${keyword.charAt(0).toUpperCase() + keyword.slice(1)} Technology`
    ];
    return keywordSubjects[Math.floor(Math.random() * keywordSubjects.length)];
  }
  
  // Otherwise use a template
  return subjectTemplates[Math.floor(Math.random() * subjectTemplates.length)];
};

// Function to assign country to email based on domain or TLD
export const assignCountryFromEmail = (email: string, targetCountries: string[] = []): string => {
  // If we have target countries, use one of those
  if (targetCountries && targetCountries.length > 0) {
    // Deterministically assign country based on email string to ensure consistency
    const emailHash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return targetCountries[emailHash % targetCountries.length];
  }
  
  // Try to extract country from email domain
  const domain = email.split('@')[1];
  
  // Check for country specific TLDs
  if (domain.endsWith('.uk')) return 'GB';
  if (domain.endsWith('.de')) return 'DE';
  if (domain.endsWith('.fr')) return 'FR';
  if (domain.endsWith('.ca')) return 'CA';
  if (domain.endsWith('.au')) return 'AU';
  if (domain.endsWith('.mt') || domain.includes('malta')) return 'MT';
  if (domain.endsWith('.in')) return 'IN';
  if (domain.endsWith('.jp')) return 'JP';
  
  // Check for common words in domains
  if (domain.includes('uk') || domain.includes('british') || domain.includes('london')) return 'GB';
  if (domain.includes('german') || domain.includes('deutsch') || domain.includes('berlin')) return 'DE';
  if (domain.includes('france') || domain.includes('french') || domain.includes('paris')) return 'FR';
  if (domain.includes('canada') || domain.includes('toronto')) return 'CA';
  if (domain.includes('australia') || domain.includes('sydney')) return 'AU';
  if (domain.includes('malta') || domain.includes('valletta')) return 'MT';
  if (domain.includes('india') || domain.includes('delhi')) return 'IN';
  if (domain.includes('japan') || domain.includes('tokyo')) return 'JP';
  
  // Default to US for .com or unknown domains
  return 'US';
};

// Function to extract name from email address for personalized greeting
export const extractNameFromEmail = (email: string): string | null => {
  if (!email) return null;
  
  // Try to extract a name from the email address
  const namePart = email.split('@')[0];
  
  // Handle common formats like first.last, first_last, etc.
  let name = namePart
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/[0-9]/g, '')
    .trim();
  
  // Capitalize each word
  if (name) {
    name = name.split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
    
    // If it's longer than 20 chars, it's probably not a name
    if (name.length > 20) return null;
    
    return name;
  }
  
  return null;
};

// Function to get polite greeting based on email
export const getPoliteGreeting = (email: string): string => {
  const name = extractNameFromEmail(email);
  
  if (name) {
    return `Hi ${name},`;
  }
  
  // For business domains, use more formal greeting
  if (email.includes('.com') || email.includes('.org') || email.includes('.net')) {
    return "Hello,";
  }
  
  return "Hi there,";
};

/**
 * Validate business email address format and domain
 */
export function isValidBusinessEmail(email: string): boolean {
  // Basic email format validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    return false;
  }

  const domain = email.split('@')[1].toLowerCase();

  // Only exclude test domains
  if (domain.includes('example.com') || domain.includes('test.com')) {
    return false;
  }

  // Check for Malta-specific domains
  const isMaltaDomain = 
    domain.endsWith('.mt') ||
    domain.endsWith('.com.mt') ||
    domain.endsWith('.org.mt') ||
    domain.endsWith('.net.mt') ||
    domain.endsWith('.edu.mt') ||
    domain.includes('malta') ||
    domain.includes('gozo') ||
    domain.includes('valletta') ||
    domain.includes('mcast') ||
    domain.includes('maltaenterprise') ||
    domain.includes('visitmalta');

  // Accept both Malta domains and general business domains
  return isMaltaDomain || domain.match(/\.(com|org|net|biz|info)$/);
}